import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { bb, Chart, ChartOptions } from 'billboard.js';
import {
    fromCamelCaseToSentence,
    generateClassNames,
    legendTmpl,
    loadType,
    tooltipTmpl
} from './bb-chart-utils';
import { combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    map,
    shareReplay,
    takeUntil,
    tap
} from 'rxjs/operators';
import {
    ExtendedChartOptions,
    ExtendedChartTypes,
    ExtendedData
} from './bb-chart.interfaces';
import { Data } from 'billboard.js/types/options';
import { Subscription } from 'rxjs/internal/Subscription';
import { StrictTitleCasePipe } from '../../pipes/strict-title-case.pipe';
import { fadeInOut } from '../../animations/fade-in-out.animation';
import { SbEventBusService } from '@services';
import { filterNilValue } from '@datorama/akita';
import { UserSettingsQuery } from 'src/app/features/user-settings/state/user-settings.query';

interface ChartData extends ExtendedData {
    json?: Array<{ [key: string]: any }>;
}
export type SbChart = Chart & {
    drilldown: (target: string, data?: { [key: string]: number }) => void;
};

@Component({
    selector: 'app-bb-chart',
    template: `
        <div
            class="bb-chart-container"
            [class.fit-height]="fitHeightToParent"
            [class.bb-cursor-pointer]="cursorPointer"
            [class.opacity-0]="loading"
            [class.mt-4]="(options$ | async)?.legend?.position === 'top-left'"
        >
            <!--<nav aria-label="Breadcrumb" *ngIf="drilldownTarget">
                <ol>
                    <li>
                        <a
                            href="#"
                            (click)="
                                $event.preventDefault();
                                drilldown(drilldownTarget);
                                drillUp.emit($event)
                            "
                            >All</a
                        >
                    </li>
                    <li>
                        <a routerLink="." aria-current="page">{{
                            drilldownTarget | titlecase
                        }}</a>
                    </li>
                </ol>
            </nav>-->
            <nav
                @fadeInOut
                aria-label="Breadcrumb"
                *ngIf="drilldownData.length > 1 && drilldownTarget"
            >
                <ol class="sb-chart-drilldown">
                    <li>
                        <tabler-icon name="chevron-left"></tabler-icon>
                        <a
                            class="align-items-center"
                            href="#"
                            (click)="$event.preventDefault(); drilldown(drilldownTarget)"
                        >
                            Back
                        </a>
                    </li>
                </ol>
            </nav>
            <div class="bb" style="opacity: 0; width: 0; height: 0">
                <svg height="60">
                    <defs>
                        <linearGradient x1="0" x2="0" y1="0" y2="1" id="bb-pattern-0">
                            <rect style="fill: rgb(0, 0, 0);"></rect>
                            <stop offset="0"></stop>
                            <stop offset="1"></stop>
                        </linearGradient>
                        <linearGradient x1="0" x2="0" y1="0" y2="1" id="bb-pattern-1">
                            <rect style="fill: rgb(0, 0, 0);"></rect>
                            <stop offset="0"></stop>
                            <stop offset="1"></stop>
                        </linearGradient>
                        <linearGradient x1="0" x2="0" y1="0" y2="1" id="bb-pattern-2">
                            <rect style="fill: rgb(0, 0, 0);"></rect>
                            <stop offset="0"></stop>
                            <stop offset="1"></stop>
                        </linearGradient>
                        <linearGradient x1="0" x2="0" y1="0" y2="1" id="bb-pattern-3">
                            <rect style="fill: rgb(0, 0, 0);"></rect>
                            <stop offset="0"></stop>
                            <stop offset="1"></stop>
                        </linearGradient>
                        <linearGradient x1="0" x2="0" y1="0" y2="1" id="bb-pattern-4">
                            <rect style="fill: rgb(0, 0, 0);"></rect>
                            <stop offset="0"></stop>
                            <stop offset="1"></stop>
                        </linearGradient>
                        <linearGradient x1="0" x2="0" y1="0" y2="1" id="bb-pattern-5">
                            <rect style="fill: rgb(0, 0, 0);"></rect>
                            <stop offset="0"></stop>
                            <stop offset="1"></stop>
                        </linearGradient>
                        <pattern
                            *ngFor="let id of patterns"
                            patternUnits="userSpaceOnUse"
                            width="6"
                            height="6"
                            [attr.id]="'bb-diagonal-stripes-' + id"
                            x="0"
                            y="0"
                        >
                            <g fill-rule="evenodd" stroke-width="1">
                                <g fill="var(--color)">
                                    <polygon points="5 0 6 0 0 6 0 5"></polygon>
                                    <polygon points="6 5 6 6 5 6"></polygon>
                                </g>
                            </g>
                        </pattern>
                        <pattern
                            patternUnits="userSpaceOnUse"
                            width="6"
                            height="6"
                            id="diagonalStripePattern"
                            x="0"
                            y="0"
                        >
                            <rect width="6" height="6" fill="white"></rect>
                            <g fill-rule="evenodd" stroke-width="1">
                                <g fill="black">
                                    <polygon points="5 0 6 0 0 6 0 5"></polygon>
                                    <polygon points="6 5 6 6 5 6"></polygon>
                                </g>
                            </g>
                        </pattern>
                        <mask
                            maskContentUnits="userSpaceOnUse"
                            id="bb-negative-value-mask"
                            x="0"
                            y="0"
                            width="1"
                            height="1"
                        >
                            <rect
                                x="-50%"
                                y="-50%"
                                width="100%"
                                height="100%"
                                fill="url(#diagonalStripePattern)"
                            />
                        </mask>
                    </defs>
                </svg>
            </div>
            <div #chartRef [class.bb-hide-grid]="(options$ | async)?.grid?.lines"></div>
            <dl
                class="legend"
                [class.legend-top-left]="
                    (options$ | async)?.legend?.position === 'top-left'
                "
                #legend
            ></dl>
        </div>
        <div @fadeInOut *ngIf="loading" class="bb-chart-loading-container">
            <ng-content></ng-content>
        </div>
        <ng-container *ngIf="data$ | async as data">
            <div
                class="d-flex align-items-center justify-content-center bb-chart-no-data"
                @fadeInOut
                *ngIf="
                    (data?.json?.length === 0 ||
                        (data?.columns && data?.columns[0]?.length === 1)) &&
                    !loading
                "
            >
                <span class="font-weight-bold">No data to show</span>
            </div>
        </ng-container>
    `,
    styleUrls: ['./bb-chart.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [fadeInOut]
})
export class BbChartComponent implements OnInit, OnDestroy, AfterViewInit {
    get loading(): boolean {
        return this._loading;
    }

    @Input() set loading(isLoading: boolean) {
        if (isLoading !== this._loading) {
            this._loading = isLoading;
            this._cdr.markForCheck();
        }
    }

    /*get loadingForced(): boolean {
        return this._loadingForced;
    }

    @Input() set loadingForced(value: boolean) {
        this._loadingForced = value;
    }*/

    get chart(): SbChart {
        return this._chart;
    }

    set chart(value: SbChart) {
        this._chart = value;
        this._cdr.detectChanges();
    }
    get options$(): Observable<ExtendedChartOptions> {
        return this._options$.asObservable().pipe(distinctUntilChanged(), shareReplay(1));
    }

    @Input() refreshEvent: Observable<void>;
    @Input() fitHeightToParent = false;
    @Input() cursorPointer = false;

    @Input() set options(value: ExtendedChartOptions) {
        if (value) {
            this._options$.next(value);
        }
    }

    get data$(): Observable<ChartData> {
        return this._data$.asObservable().pipe(distinctUntilChanged(), shareReplay(1));
    }

    @Input() set data(value) {
        if (value) {
            this._data$.next(value);
        }
    }

    @Output() chartRef = new EventEmitter<SbChart>();
    @Output() drillUp = new EventEmitter<MouseEvent>();
    @Output() loadingChange = new EventEmitter<boolean>();

    constructor(
        private _cdr: ChangeDetectorRef,
        private strictTitleCase: StrictTitleCasePipe,
        private _ngZone: NgZone,
        private _eventBus: SbEventBusService,
        private _userSettingsQuery: UserSettingsQuery
    ) {
        // redraw chart on print event (enter and exit print mode)
        this._eventBus
            .getObservable('Print')
            .pipe(
                filterNilValue(),
                debounceTime(300),
                tap((_) => this.chart?.flush()),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();
    }
    unsubscribe$ = new Subject();
    eventsSubscription: Subscription;

    patterns = Array(10)
        .fill(null)
        .map((_, i) => i);
    private _loading?: boolean;
    //private _loadingForced?: boolean;
    private _options$: ReplaySubject<ExtendedChartOptions> =
        new ReplaySubject<ExtendedChartOptions>(1);
    private _data$: ReplaySubject<ChartData> = new ReplaySubject<ChartData>(1);
    private _chartElementRef$: ReplaySubject<HTMLElement> =
        new ReplaySubject<HTMLElement>(1);
    private _chart: SbChart;
    @ViewChild('chartRef') _chartElementRef: ElementRef | undefined;
    @ViewChild('legend') _legendElementRef: ElementRef | undefined;

    ngOnInit() {
        this.setupRefreshListener();

        combineLatest([this.data$, this.options$, this._chartElementRef$])
            .pipe(
                map(
                    ([data, options, elmRef]): [
                        Partial<ExtendedChartOptions>,
                        ChartData
                    ] => {
                        let refinedOptions: Partial<ExtendedChartOptions> = {};

                        // add options based on data types
                        if (options.data.types) {
                            Object.values(options.data.types).forEach(
                                (type: ExtendedChartTypes) => {
                                    refinedOptions = {
                                        ...refinedOptions,
                                        ...options,
                                        ...loadType(type, options, data)
                                    };
                                }
                            );
                        }
                        // add options based on base data type
                        if (options.data.type) {
                            refinedOptions = {
                                ...refinedOptions,
                                ...options,
                                ...loadType(options.data.type, options, data)
                            };
                        }
                        // create data options
                        const dataOptions: ExtendedData = {
                            ...options.data, // add base data options
                            ...data, // add pure data
                            ...refinedOptions.data // add refined data options
                        };
                        refinedOptions = {
                            ...refinedOptions,
                            data: {
                                ...dataOptions,
                                classes:
                                    dataOptions.classes ||
                                    generateClassNames(<Data>dataOptions)
                            },
                            grid: {
                                front: false,
                                x: {
                                    show: true,
                                    ...(options.grid?.x || {})
                                },
                                y: {
                                    show: true,
                                    ...(options.grid?.y || {})
                                }
                            },
                            color: {
                                ...refinedOptions.color,
                                pattern: refinedOptions.color?.tiles
                                    ? ['#000', '#000', '#000', '#000', '#000', '#000']
                                    : [null]
                            },
                            bindto: elmRef,
                            tooltip: {
                                show: true,
                                doNotHide: false,
                                grouped: true,
                                format: {
                                    name: (name) => fromCamelCaseToSentence(name)
                                },
                                contents: (data) =>
                                    tooltipTmpl(
                                        data,
                                        refinedOptions.data?.classes,
                                        refinedOptions.data?.mappings
                                    ),
                                ...refinedOptions.tooltip
                            },
                            legend: {
                                show:
                                    options.legend?.show !== undefined
                                        ? options.legend.show
                                        : true,
                                contents: {
                                    bindto: this._legendElementRef.nativeElement,
                                    template: (id) =>
                                        legendTmpl(id, this.chart, {
                                            show: options?.legend?.showPercentages,
                                            locale: this._userSettingsQuery.getGlobalSetting(
                                                'LOCALE'
                                            )
                                        })
                                },
                                ...options.legend
                            },
                            point: {
                                focus: {
                                    //only: true
                                },
                                ...options.point
                            },
                            padding: {
                                mode: 'fit',
                                left: 0,
                                right: 0
                            },
                            boost: {
                                useWorker: true
                            },
                            oninit: () => {
                                this._ngZone.run(() => {
                                    this.loading = true;
                                    this.loadingChange.emit(true);
                                });
                            },
                            onrendered: () => {
                                this._ngZone.run(() => {
                                    this.loading = false;
                                    this.loadingChange.emit(false);
                                });
                            }
                        };
                        const refinedData = <ChartData>{
                            ...data,
                            ...refinedOptions.data
                        };
                        return [refinedOptions, refinedData];
                    }
                )
            )
            .pipe(debounceTime(0), takeUntil(this.unsubscribe$))
            .subscribe(([options, data]) => {
                this._ngZone.runOutsideAngular(() => {
                    if (this.chart && data) {
                        if (options.resetChart) {
                            this.loading = true;
                            try {
                                this.chart?.destroy();
                            } catch (e) {
                                console.log(e);
                            }
                            this.chart = bb.generate(<ChartOptions>options) as SbChart;
                        } else {
                            this.loading = true;
                            if (Array.isArray(data.hide)) {
                                try {
                                    this.chart?.hide(
                                        (data.hide as Array<string>)?.filter(
                                            (seriesName) => seriesName.endsWith('_value')
                                        ),
                                        {
                                            withLegend: true
                                        }
                                    );
                                } catch (e) {
                                    console.log(e);
                                }
                            }
                            //this.chart.legend.hide();
                            this.chart?.load({
                                ...(<any>data),
                                unload: true,
                                done: () => {
                                    this._ngZone.run(() => {
                                        this.loading = false;
                                        this.loadingChange.emit(false);
                                    });
                                }
                            });
                        }
                    } else {
                        this.chart = bb.generate(<ChartOptions>options) as SbChart;
                    }
                });
                this.chartRef.next(this.chart);

                // add drilldown function if not present
                if (!this.chart['drilldown']) {
                    this.chart['drilldown'] = (name, target) =>
                        this.drilldown(name, target);
                }
                // set size
                //this.chart.resize();
            });
    }

    setupRefreshListener() {
        if (this.refreshEvent) {
            this.eventsSubscription = this.refreshEvent.subscribe(() => {
                setTimeout(() => {
                    if (this.chart) {
                        this.chart.flush();
                    }
                });
            });
        }
    }

    drilldownData = [];
    drilldownTarget;
    drilldown(name: string, data?, initialName?: string) {
        const drillUp = !data;
        const currentData = this.chart.data();
        const classNames = this.chart.config('data.classes');
        if (this.drilldownData.length === 0) {
            const data = currentData.reduce(
                (acc, current) => ({
                    ...acc,
                    [current.id]:
                        (classNames[current.id].indexOf('bb-target-negative-value') > -1
                            ? -1
                            : 1) * current.values[0].value
                }),
                {}
            );
            this.drilldownData = [
                ...this.drilldownData,
                { name: initialName || 'All', data }
            ];
        }

        if (drillUp && this.drilldownData.length > 0) {
            this.drilldownData = [...this.drilldownData.slice(0, -1)];
        } else {
            this.drilldownData = [...this.drilldownData, { name, data }];
        }

        const newData = this.drilldownData[this.drilldownData.length - 1].data;
        const newName = this.drilldownData[this.drilldownData.length - 1].name;
        const value = Object.keys(newData);
        const absoluteValues = {};
        Object.entries<number>(newData || {}).forEach(([key, value], index) => {
            absoluteValues[key] = Math.abs(value);
        });
        const classes = value.reduce(
            (acc, key, index) => ({
                ...acc,
                [key]: `group-color-${index + 1} value-color-1${
                    newData[key] < 0 ? ' bb-target-negative-value' : ''
                }`
            }),
            {}
        );
        this.chart.load({
            json: [absoluteValues],
            keys: {
                value
            },
            classes,
            unload: currentData.map((data) => data.id)
        });

        this.chart.$.main
            .select('.bb-chart-arcs-title')
            .text(this.strictTitleCase.transform(newName));
        this.drilldownTarget = newName;
        if (drillUp) {
            this.drillUp.emit(newData);
        }
        this._cdr.detectChanges();
    }

    ngAfterViewInit(): void {
        this._chartElementRef$.next(this._chartElementRef.nativeElement);
    }

    ngOnDestroy(): void {
        this.eventsSubscription?.unsubscribe();
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
        try {
            this.chart?.destroy();
        } catch (e) {
            //console.error(e);
        }
    }
}
